import { AliasPageName, GamificationPages } from 'src/app/gamification/cv-marketplace-admin/models/gamification-pages.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CvCustomDialogService } from '../../../../reusables/custom-dialog/cv-custom-dialog.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { Injectable } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { PermissionType, PermissionFilter } from '../../../../../AngularJS/Goals/Gamification/Models/permission-filter';

@Injectable({
  providedIn: 'root'
})
export class PointStorePermissionService {

  modalOptions: NgbModalOptions = {
    keyboard: false,
    backdrop: 'static',
    centered: true,
    windowClass: 'cxone-modal-auth',
  };

  private _unauthorizedMessage = 'Unauthorized Access. You do not have the proper permissions to complete this action.';
  private _edit = 'WRITE';
  private _create = 'CREATE';
  private _view = 'READ';
  private _permissions: Map<string, string[]> = new Map();

  get unauthorizedMessage(): string {
    return this._unauthorizedMessage;
  }

  getEditPermissionName(): string {
    return this._edit;
  }

  getCreatePermissionName(): string {
    return this._create;
  }

  getViewPermissionName(): string {
    return this._view;
  }

  constructor(
    private cvApiService: CVApiService,
    private dialogService: CvCustomDialogService,
    private cvLocalizationService: CVLocalizationService,
    private cvConfigurationService: CvConfigurationService,
  ) {
    this.cvLocalizationService.GetSection("Common");
    this.cvLocalizationService.GetSection("Gamification_Admin");
  }

  async loadPermission(pageName: string) {
    try {
      const url = "/api/v1/gamification/permissions";
      const { data, message: prompt } = await this.cvApiService.post(url, { type: 'list', pageName });
      this._permissions.set(pageName, data as string[]);
    } catch (error) {
      console.warn(error.message);
    }
  }

  checkPermission(permision: string, pageName: string): boolean {
    let check: string[] = [];
    if (this._permissions && this._permissions.has(pageName)) {
      const permissionsList: string[] = this._permissions.get(pageName);
      check = permissionsList?.filter((p: string) => p === permision);
    }
    return check?.length > 0;
  }

  async getPermission(permissionFilter: PermissionFilter) {
    try {
      const url = "/api/v1/gamification/permissions";
      const { data, status_code, message: prompt } = await this.cvApiService.post(url, permissionFilter);
      if (status_code === 200) {
        return data;
      } else {
        if (this.cvConfigurationService.isCxOne) {
          if (permissionFilter.newCXoneModalVersion) {
            this.dialogService.showDialog(
              this.getLocalizationString("Unauthorized_Access", "Unauthorized Access"),
              this.getLocalizationString("Permission_Empty_Data",
                "Unauthorized Access. You do not have the proper permissions to complete this action."
              ),
              14,
              this.cvLocalizationService.getCachedText("Common", "Cancel_Button", "Cancel"),
              this.cvLocalizationService.getCachedText("Common", "OK", "OK"),
              true,
              () => { },
              () => { },
              '',
              true,
              this.modalOptions);
          } else {
            this.dialogService.showDialog(
              this.getLocalizationString("Unauthorized_Access", "Unauthorized Access"),
              this.getLocalizationString("Permission_Empty_Data",
                "Unauthorized Access. You do not have the proper permissions to complete this action."
              ),
              1,
              this.cvLocalizationService.getCachedText("Common", "Cancel_Button", "Cancel"),
              this.cvLocalizationService.getCachedText("Common", "OK", "OK"),
              true);
          }
        } else {
          await this.validationMessage(this.getLocalizationString("Permission_Empty_Data",
            "Unauthorized Access. You do not have the proper permissions to complete this action."
          ));
        }
      }
    } catch (error) {
      console.warn(error.message);
    }
  }

  validationMessage(prompt: string) {
    return new Promise((resolve, reject) => {

      let validation = new window["CVVALIDATION"].ValidationInstance();
      validation.succeeded = false;
      validation.addValidationError(prompt, true);

      window["showConfirmDialog"]("<ul class='errorBulletList'>" + validation.messagesToListItemsString() + "</ul>",
        [
          {
            text: this.cvLocalizationService.getCachedText("Common", "OK", "OK"),
            click: () => {
              let dialogMsg: any = $(".errorDialog");
              dialogMsg.dialog("close");
              return resolve(true);
            }
          },
          {
            text: this.cvLocalizationService.getCachedText("Common", "Cancel_Button", "Cancel"),
            click: () => {
              let dialogMsg: any = $(".errorDialog");
              dialogMsg.dialog("close");
              return reject(false);
            }
          }
        ], this.getLocalizationString("Unauthorized_Access", "Unauthorized Access"), "Warning");
    });
  }

  /**
   *
   * @param permissionName WRITE, CREATE
   * @param aliasPageName MarketplaceManagement, GamesTab, AwardsTab will return the pageName depeding of isCxOne flag
   * @returns permissionName and pageName
   */
  pageNameFilter(aliasPageName: AliasPageName): string {
    const pageNames: Record<string, string> = {
      marketplaceManagement: this.cvConfigurationService.isCxOne
        ? GamificationPages.MarketplaceManagementCXOne
        : GamificationPages.MarketplaceManagementPM,
      gamesTab: this.cvConfigurationService.isCxOne
        ? GamificationPages.GamesTabCXOne
        : GamificationPages.GamesTabPM,
      awardsTab: this.cvConfigurationService.isCxOne
        ? GamificationPages.AwardsTabCXOne
        : GamificationPages.AwardsTabPM,
      budgetTab: this.cvConfigurationService.isCxOne
        ? GamificationPages.BudgetTabCXOne
        : GamificationPages.BudgetTabPM,
      challengesTab: this.cvConfigurationService.isCxOne
        ? GamificationPages.ChallengesTabCXOne
        : GamificationPages.ChallengesTabPM
    };
    return pageNames[aliasPageName] ?? null;
  }

  /**
   *
   * @param permissionFilter type: [PermissionFilter].
   * @param cxoneOnly type: [boolean]. Validation is performed only for cxone
   * @param permissionType type: [PermissionFilter]. If it's multiple, you need to send all the permissions you want to validate, separated by commas. If you need to validate just one permission, use 'single'.
   * @returns
   */
  async validatePermission(
    permissionFilter: PermissionFilter,
    cxoneOnly: boolean = false,
    permissionType: PermissionType = 'single'
  ): Promise<boolean> {
    if (cxoneOnly) {
      return true;
    }
    let hasPermission = false;
    const permission = await this.getPermission({
      type: permissionType,
      ...permissionFilter
    });
    if (permission) {
      hasPermission = true;
    }
    return hasPermission;
  }

  private getLocalizationString(key: string, defaultValue: string) {
    return this.cvLocalizationService.getCachedText("Gamification_Admin", key, defaultValue);
  }
}
